// orphaned-supervisor-lock-release.ts
//
// Phase 05 (AC-02): `avatar autopilot unlock` — releases the machine lock,
// journal lease, active-worker record, and (BUG-2) the stale attempt binding
// left by a hard SIGKILL/crash of the supervisor. EVERY refusal branch
// decides and returns BEFORE any kill/delete (§15 fail-closed).
import { readFile, unlink } from "node:fs/promises";
import { join } from "node:path";
import { type ActiveWorkerRecord, readActiveWorker } from "./active-worker-record.js";
import { classifyJournalLeaseOwner } from "./journal-lease-owner-liveness.js";
import {
  EXISTING_LOCK_READ_RETRIES,
  EXISTING_LOCK_READ_RETRY_DELAY_MS,
  type MachineLockFile,
} from "./mission-lock.js";
import { cleanupStaleOrphanedBinding } from "./orphaned-ticket-recovery-applier.js";
import { deadOrPidReused, type LockLivenessProbe } from "./process-liveness-probe.js";

export type OrphanedSupervisorLockReleaseStatus =
  | "refused_supervisor_alive"
  | "refused_other_mission"
  | "refused_journal_lease_held"
  | "refused_child_alive"
  | "refused_lock_unreadable"
  | "released"
  | "nothing_to_release";

export interface ReleaseOrphanedSupervisorLocksInput {
  machineLockPath: string;
  controlRoot: string;
  missionId: string;
  liveness: LockLivenessProbe;
  killGroup: (pgid: number) => Promise<void>;
  processGroupId?: (pid: number) => Promise<number | undefined>;
  /** BUG-2: when set, a successful release also reclaims the stale attempt binding (see bottom of the function). Optional for backward compat. */
  missionWorkspace?: string;
}

export interface ReleaseOrphanedSupervisorLocksResult {
  status: OrphanedSupervisorLockReleaseStatus;
  removed: string[];
  killedWorkerPgid?: number;
  hint?: string;
}

async function readLockWithRetry(
  lockPath: string,
): Promise<MachineLockFile | undefined | "unreadable"> {
  for (let attempt = 0; attempt <= EXISTING_LOCK_READ_RETRIES; attempt++) {
    try {
      return JSON.parse(await readFile(lockPath, "utf8")) as MachineLockFile;
    } catch (err) {
      if ((err as NodeJS.ErrnoException).code === "ENOENT") return undefined;
      if (attempt === EXISTING_LOCK_READ_RETRIES) return "unreadable";
      await new Promise((r) => setTimeout(r, EXISTING_LOCK_READ_RETRY_DELAY_MS));
    }
  }
  return "unreadable";
}

export async function releaseOrphanedSupervisorLocks(
  input: ReleaseOrphanedSupervisorLocksInput,
): Promise<ReleaseOrphanedSupervisorLocksResult> {
  const { machineLockPath, controlRoot, missionId, liveness } = input;
  const leasePath = join(controlRoot, "locks", "journal.lock");

  // (1) B9: lock unreadable ⇒ refuse before anything else.
  const lock = await readLockWithRetry(machineLockPath);
  if (lock === "unreadable") {
    return {
      status: "refused_lock_unreadable",
      removed: [],
      hint:
        `Lock ${machineLockPath} tồn tại nhưng không đọc được. (1) \`pgrep -fl "autopilot run"\` phải rỗng ` +
        `(có dòng ⇒ đợi/dừng, KHÔNG xoá); (2) \`rm ${machineLockPath}\`; (3) \`avatar autopilot run\` lại.`,
    };
  }

  // (2) B2: lock belongs to a different mission ⇒ refuse, touch nothing.
  if (lock && lock.missionId !== missionId) {
    return {
      status: "refused_other_mission",
      removed: [],
      hint:
        `Lock ${machineLockPath} thuộc mission "${lock.missionId}" — chạy ` +
        `\`avatar autopilot unlock --mission ${lock.missionId}\` trong workspace chứa mission đó.`,
    };
  }

  const record: ActiveWorkerRecord | undefined = await readActiveWorker(controlRoot);
  const workerAlive = record ? await liveness.isAlive(record.pid) : false;

  // (3) supervisor liveness — from the machine lock if present, else (N5)
  // from the active-worker record's own supervisor_pid/started_at.
  let supervisorDead: boolean;
  if (lock) {
    supervisorDead = await deadOrPidReused(liveness, lock.pid, lock.processStartedAt);
  } else if (record && workerAlive) {
    supervisorDead = await deadOrPidReused(
      liveness,
      record.supervisor_pid,
      record.supervisor_started_at,
    );
  } else {
    supervisorDead = true;
  }
  if (!supervisorDead) return { status: "refused_supervisor_alive", removed: [] };

  // (4) B1: journal lease owner.
  const lease = await classifyJournalLeaseOwner({
    leasePath,
    machineLock: lock,
    supervisorDead,
    liveness,
  });
  if (lease.state === "owner_alive_or_unknown") {
    return {
      status: "refused_journal_lease_held",
      removed: [],
      hint:
        `Lease ${leasePath} (pid ${lease.pid}) — \`ps -p ${lease.pid} -o command=\` không phải ` +
        "`avatar autopilot` ⇒ xoá đúng file lease đó rồi `run` lại.",
    };
  }

  // (5) B7/N4: any lock-recorded child pid still alive, outside the worker group.
  for (const pid of lock?.childPids ?? []) {
    if (!(await liveness.isAlive(pid))) continue;
    const pgid = await input.processGroupId?.(pid);
    if (!record || pgid === undefined || pgid !== record.pgid) {
      return {
        status: "refused_child_alive",
        removed: [],
        hint: `Tiến trình con pid ${pid} còn sống, ngoài nhóm worker đã ghi — dừng pid ${pid} rồi chạy lại \`avatar autopilot unlock\`.`,
      };
    }
  }

  // (5b) N1 s4: recorded worker itself still alive — only kill when identity confirmed.
  let killPgid: number | undefined;
  if (record && workerAlive) {
    const now = await liveness.processStartedAt(record.pid);
    if (record.worker_started_at === undefined || now === undefined) {
      return {
        status: "refused_child_alive",
        removed: [],
        hint:
          `Worker pid ${record.pid} (pgid ${record.pgid}, \`kill -TERM -${record.pgid}\`) còn sống nhưng chưa xác minh được — ` +
          `xác nhận \`ps -o command= -p ${record.pid}\` là worker \`claude\` của mission này rồi \`kill -TERM -${record.pgid}\`, ` +
          "sau đó chạy lại `avatar autopilot unlock`.",
      };
    }
    if (now === record.worker_started_at) killPgid = record.pgid;
  }

  // (6)/(7) kill confirmed worker group, then remove every lock file present.
  const removed: string[] = [];
  let killedWorkerPgid: number | undefined;
  if (killPgid !== undefined) {
    await input.killGroup(killPgid);
    killedWorkerPgid = killPgid;
  }
  if (lock) {
    await unlink(machineLockPath).catch(() => {});
    removed.push(machineLockPath);
  }
  if (lease.state === "owner_dead") {
    await unlink(leasePath).catch(() => {});
    removed.push(leasePath);
  }
  if (record) {
    await unlink(join(controlRoot, "active-worker.json")).catch(() => {});
    removed.push(join(controlRoot, "active-worker.json"));
  }

  // BUG-2 fix: every refusal above already returned => no live writer left.
  // Reclaim the stale binding NOW so the NEXT `run` reclaims the stuck
  // in_progress ticket immediately instead of waiting for expires_at.
  if (input.missionWorkspace) await cleanupStaleOrphanedBinding(input.missionWorkspace);

  return {
    status: removed.length > 0 ? "released" : "nothing_to_release",
    removed,
    ...(killedWorkerPgid !== undefined ? { killedWorkerPgid } : {}),
  };
}
